import React, { useEffect, useRef, useState } from "react";
import styles from "./OtpInput.module.css";

const OTP_LENGTH = 6;

const OtpInput = ({
  name = "otp",
  otp = "",
  setOtp = () => {},
  isDisable = false,
  placeholder = "Enter OTP",
  error = "",
  onChange,
  onKeyDown,
  onAutoFill,
  autoFillKey = 0,
}) => {
  const inputRef = useRef(null);
  // idle = autofill not available, waiting = listening for the SMS (the browser
  // asks the user for permission when it arrives), filled = code came from SMS.
  const [smsStatus, setSmsStatus] = useState("idle");

  // Latest callbacks in refs, so the SMS listener does not have to re-subscribe
  // every render (that would drop an in-flight permission prompt).
  const setOtpRef = useRef(setOtp);
  const onAutoFillRef = useRef(onAutoFill);
  setOtpRef.current = setOtp;
  onAutoFillRef.current = onAutoFill;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // WebOTP API (Chrome on Android). Started as soon as the OTP screen opens so
  // the request is already active when the SMS lands — the browser then shows
  // its own "Verify your phone number?" permission prompt, and only if the user
  // allows it do we get the code. Re-armed via autoFillKey after a resend.
  // Needs https + an SMS whose last line is "@<domain> #<code>".
  useEffect(() => {
    if (isDisable) return;
    if (typeof window === "undefined" || !("OTPCredential" in window)) return;
    if (!navigator.credentials?.get) return;

    const controller = new AbortController();
    setSmsStatus("waiting");

    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: controller.signal })
      .then((credential) => {
        const code = (credential?.code || "")
          .replace(/\D/g, "")
          .slice(0, OTP_LENGTH);
        if (!code) {
          setSmsStatus("idle");
          return;
        }
        setOtpRef.current(code);
        setSmsStatus("filled");
        if (code.length === OTP_LENGTH) onAutoFillRef.current?.(code);
      })
      .catch(() => {
        // Permission denied, prompt dismissed, or request aborted — fall back
        // to manual entry / the keyboard's one-time-code suggestion.
        setSmsStatus("idle");
      });

    return () => controller.abort();
  }, [isDisable, autoFillKey]);

  const handleChange = (e) => {
    // Strip anything non-numeric rather than rejecting the value: autofilled
    // and pasted codes can arrive with spaces or surrounding text.
    const value = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);

    setOtp(value);
    if (onChange) onChange(e);
  };

  return (
    <>
    <div className={["input-group"].join(" ")}>
      <span class="input-group-text bg-dark" id="basic-addon1" style={{borderRadius:"30px 0px 0px 30px",backgroundImage:"linear-gradient(to right, #26B9DB, #5D99E7)",animationDuration:"2s"}} ><i class="fa-solid text-white fa-mobile-screen-button fa-beat"></i></span>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={otp}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        placeholder="Enter OTP"
        disabled={isDisable}
        inputMode="numeric"
        maxLength={OTP_LENGTH}
        autoComplete="one-time-code"
        autoCorrect="off"
        spellCheck="false"
        className={`${styles.singleInput} ${error ? styles.error : ""} ${"shadow-none"} ${"border-0 border"} ${"text-start"} ${"form-control"}`}
      />
    </div>
      {error && <p className={styles.errorText}>{error}</p>}
      {!error && smsStatus === "waiting" && (
        <p className={styles.autoFillHint}>
          Waiting for the SMS — allow the autofill prompt and we'll fill the OTP for you.
        </p>
      )}
      {!error && smsStatus === "filled" && (
        <p className={styles.autoFillDone}>OTP filled automatically from SMS</p>
      )}
      </>
  );
};

export default OtpInput;
